import React, { useEffect, useState } from "react";
import api from "../../chat/api";
import Shell from "./CopilotCardShell";
import ShowDocumentButton from "./ShowDocumentButton";

/* A section is written. Keep it, or ask for another pass.
 *
 * WHAT THIS CARD IS FOR. One decision: continue, or change it. Everything else
 * is supporting detail, and the earlier version had that backwards - three
 * 22px stat numbers were the loudest thing on screen, outranking the question
 * the card exists to ask. Word count is reassurance, not a decision input; the
 * document beside it is what tells you whether the section is any good.
 *
 * So the card is a header and a decision - two bands, no third. The stats sit
 * in the header as context for the title, and there is no body at all unless a
 * rewrite is being described. Twelve sections means seeing this twelve times;
 * every row that does not earn its place is paid for twelve times over.
 *
 * AND THERE IS A WAY OUT OF THE LOOP. "Write the rest without asking" exists
 * because by section three most people have calibrated and the gate has become
 * theatre - a click they make without reading. A gate nobody reads is worse
 * than no gate, because it looks like review.
 *
 * NO REQUIREMENT COUNT. Sections carry no requirement linkage, so that number
 * could only be invented - and an invented number on the card people use to
 * judge a section is worse than one fewer number.
 */

const ProposalSectionCard = ({ message, onApprove, onRewrite, onAutoContinue, docOpen, onOpenDoc }) => {
  const {
    projectId,
    sectionTitle,
    sectionIdx = 0,
    subsections = 0,
    words = 0,
    isLast,
    nextTitle,
    totalSections = 0,
  } = message || {};

  /* How many sections this card's shortcut would write.
   *
   * `sectionIdx` is zero-based and this one is already drafted, so the rest is
   * everything after it. Guarded to zero when the total is unknown — the label
   * falls back to "the rest" rather than promising a count it cannot stand by.
   */
  const remaining = Math.max(0, (totalSections || 0) - (sectionIdx + 1));

  const [stats, setStats] = useState({ words, sources: 0 });
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  /* Real counts for the section just written, from the same lean endpoint the
   * outline and writing cards read. Seeded from the draft that just ran so the
   * line is never empty while this is in flight. */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/api/v1/rfp/folders/${projectId}/proposal/outline/confirm/`);
        const sec = (res?.data?.toc || [])[sectionIdx];
        if (!alive || !sec) return;
        const nodes = [sec, ...(sec.children || [])];
        setStats({
          words: nodes.reduce((a, n) => a + (n.words || 0), 0),
          sources: nodes.reduce((a, n) => a + (n.sources || 0), 0),
        });
      } catch {
        /* keep the seeded numbers */
      }
    })();
    return () => {
      alive = false;
    };
  }, [projectId, sectionIdx]);

  /* There is deliberately NO global Enter shortcut.
   *
   * There was one, and it was a mistake. A window-level keydown that approves
   * cannot reliably tell "the user pressed Enter meaning approve" from "the
   * user pressed Enter to send a message" — the composer is live during this
   * step, and the guard on document.activeElement is one blur or one IME
   * composition away from being wrong.
   *
   * Approving starts minutes of model work on the next section. A keystroke
   * that expensive must not be inferred; it has a button, and the button is not
   * hard to reach.
   */

  const rewrite = () => {
    setBusy(true);
    onRewrite?.({ instruction: note.trim() });
  };

  const ghost =
    "inline-flex items-center gap-1.5 rounded-lg border border-[#18101C]/[0.12] bg-white px-2.5 py-1.5 text-[12.5px] font-medium text-[#3F3F46] transition-colors duration-150 hover:border-[#18101C]/25 hover:bg-[#18101C]/[0.03] disabled:cursor-not-allowed disabled:opacity-55 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none";
  const primary =
    "inline-flex items-center gap-2 rounded-lg bg-gradient-to-br from-[#A64E9D] to-[#8E4187] px-3.5 py-1.5 text-[13px] font-semibold text-white ring-1 ring-inset ring-white/[0.16] shadow-[0_1px_2px_rgba(24,16,28,0.10),0_8px_18px_-10px_rgba(142,65,135,0.65)] transition-[transform,box-shadow,filter] duration-200 hover:-translate-y-[1px] hover:brightness-[1.04] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2 motion-reduce:transition-none";
  const quiet =
    "text-[12.5px] font-medium text-[#6B6472] underline decoration-[#6B6472]/25 decoration-1 underline-offset-[3px] transition-colors hover:text-[#18101C] hover:decoration-[#18101C]/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none";

  const counted = [
    `${stats.words.toLocaleString()} words`,
    subsections ? `${subsections} subsection${subsections === 1 ? "" : "s"}` : "",
    stats.sources ? `${stats.sources} source${stats.sources === 1 ? "" : "s"}` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Shell
      width={640}
      icon={
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      }
      // "3 of 12", not "Section 3". The bare number answers nothing about how
      // much is left, which is the thing someone three sections in wants to know.
      title={
        totalSections
          ? `${sectionIdx + 1} of ${totalSections} · ${sectionTitle || ""}`
          : sectionTitle || "Section written"
      }
      /* The stats live in the header, not in a band of their own.
       *
       * One short line of muted text does not earn a walled section with a rule
       * above and below it — that was three bands for a card carrying one
       * decision. As header meta the numbers read as context for the title,
       * which is what they are. */
      meta={
        <span className="flex items-center gap-2.5">
          <span className="hidden text-[11.5px] tabular-nums text-[#77707E] sm:inline">
            {counted}
          </span>
          <ShowDocumentButton
            open={docOpen}
            onOpen={onOpenDoc}
            iconOnly
            label="Read it in the document"
          />
        </span>
      }
      footer={
        asking ? (
          <>
            <button type="button" onClick={rewrite} disabled={busy} className={primary}>
              {busy && (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-[2px] border-white/40 border-t-white motion-reduce:animate-none" />
              )}
              {note.trim() ? "Rewrite with this" : "Rewrite from scratch"}
            </button>
            <button type="button" onClick={() => setAsking(false)} disabled={busy} className={ghost}>
              Cancel
            </button>
          </>
        ) : (
          <div className="flex w-full flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onApprove?.()}
              title="Approve and write the next section"
              className={primary}
            >
              {isLast ? "Approve — that's the last one" : "Approve & continue"}
            </button>

            <button type="button" onClick={() => setAsking(true)} className={ghost}>
              <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                <path d="M3 3v5h5" />
              </svg>
              Rewrite
            </button>

            {/* "Next: …" rather than a separate line under the buttons. It
                describes what Approve starts, so it belongs beside Approve —
                and it was the fifth piece of text on a card with one decision.
                Truncates instead of wrapping: a long section title must not
                push the card taller. */}
            {/* The next section's name, given the room to actually be read.
                It was competing with a 29-character link and losing — truncated
                to "Company Profile and Releva…", which tells you less than
                nothing. Dropping the "Next:" prefix and shortening the link
                beside it buys back most of a button's width. */}
            {!isLast && nextTitle && (
              <span
                title={`Next: ${nextTitle}`}
                className="ml-1 hidden min-w-0 flex-1 truncate text-[12px] text-[#8A8391] md:inline"
              >
                Next: {nextTitle}
              </span>
            )}

            {/* "Edit by hand" is gone. It opened a new tab and abandoned a flow
                that is mid-run — a heavy, rare action sitting next to the two
                everyday ones. The same link lives on the completion card and in
                the document panel, which is where someone actually goes to edit. */}
            {/* Named for what it DOES, not for what it stops doing.
             *
             * "Don't ask again" described the side effect — the gate going away
             * — and left the actual consequence unsaid: that pressing it starts
             * writing every remaining section, which on a fourteen-section
             * proposal is a long, expensive run. A control whose effect you
             * only discover afterwards is the wrong control to make quiet, so
             * it says the work it starts, and how much of it. */}
            {!isLast && onAutoContinue && (
              <button
                type="button"
                onClick={onAutoContinue}
                title="Approve this one and write every remaining section without stopping to ask"
                className={`${quiet} ml-auto flex-none`}
              >
                {remaining > 0 ? `Draft all ${remaining} remaining` : "Draft the rest"}
              </button>
            )}
          </div>
        )
      }
    >
      {/* Nothing here unless a rewrite is being described.
          Rendering on `counted` left a padded, ruled band on every desktop card
          whose only child was sm:hidden — an empty third band, which is exactly
          the height this card did not need. The stats live in the header and
          simply do not appear on narrow screens; they are reassurance, not
          something the decision depends on. */}
      {asking && (
      <div className="px-4 py-2.5">
        {asking && (
          <div className="animate-sidebar-fade-in motion-reduce:animate-none">
            <textarea
              autoFocus
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What should change? e.g. make the SLA paragraph firmer"
              className="w-full resize-none rounded-xl border border-[#18101C]/[0.12] bg-white px-3 py-2 text-[13px] leading-relaxed text-[#18101C] placeholder:text-[#8A8391] focus:border-[#A04A97]/50 focus:outline-none focus:ring-2 focus:ring-[#A04A97]/20"
            />
            <p className="mt-1.5 text-[11.5px] text-[#77707E]">
              Leave it empty to simply draft it again.
            </p>
          </div>
        )}
      </div>
      )}
    </Shell>
  );
};

export default ProposalSectionCard;
